import { motion } from "framer-motion";
import {
  TrendingUp, CloudSun, ShoppingCart, Sparkles, Package, Target,
  Leaf, Zap, CheckCircle2, ArrowUpRight, BarChart3, Clock, AlertTriangle
} from "lucide-react";

export default function Bento() {
  return (
    <section id="product" className="max-w-6xl mx-auto px-6 py-24 md:py-32 scroll-mt-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl"
      >
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
          <Zap className="h-3.5 w-3.5" /> Engineered for Modern Cafés
        </div>
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-zinc-900 mt-4">
          Everything your café needs to run with clockwork precision.
        </h2>
        <p className="text-zinc-500 mt-3 text-base">
          Replace disjointed spreadsheets and intuition with automated intelligence that connects your kitchen, baristas, and suppliers.
        </p>
      </motion.div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {/* Tile 1: Product-level demand forecasting (Large 2-column) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45 }}
          className="lg:col-span-2 bg-white border border-zinc-200/90 rounded-2xl p-7 shadow-card flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <div className="h-10 w-10 rounded-xl bg-zinc-900 grid place-items-center">
                <TrendingUp className="h-5 w-5 text-amber-400" />
              </div>
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                Daily forecasts with MAPE tracking
              </span>
            </div>

            <h3 className="font-display font-semibold text-xl text-zinc-900 mt-5">
              Daily product-level demand forecasting
            </h3>
            <p className="text-sm text-zinc-500 mt-2 leading-relaxed max-w-lg">
              Predicts exact quantities for every coffee blend, cold beverage, sandwich, and pastry on your menu by analyzing historical sales, 7-day weather, school holidays, and local sports matches.
            </p>
          </div>

          {/* Mini UI Visualization */}
          <div className="mt-6 bg-zinc-950 text-white rounded-xl p-4 border border-zinc-800">
            <div className="flex items-center justify-between text-xs text-zinc-400 pb-2 border-b border-zinc-800">
              <span className="font-medium text-zinc-200">Tomorrow's Predicted Menu Volume</span>
              <span className="text-emerald-400 font-semibold">+18% Rain Surge</span>
            </div>
            <div className="mt-3 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-300">Flat White / Latte (Regular)</span>
                <span className="font-mono text-amber-400 font-bold">142 cups</span>
              </div>
              <div className="w-full bg-zinc-800 h-2 rounded-full overflow-hidden">
                <div className="bg-amber-500 h-full w-[82%]" />
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-zinc-300">Iced Long Black / Cold Brew</span>
                <span className="font-mono text-sky-400 font-bold">28 cups</span>
              </div>
              <div className="w-full bg-zinc-800 h-2 rounded-full overflow-hidden">
                <div className="bg-sky-500 h-full w-[35%]" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tile 2: Weather Sensitivity Intelligence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="bg-white border border-zinc-200/90 rounded-2xl p-7 shadow-card flex flex-col justify-between"
        >
          <div>
            <div className="h-10 w-10 rounded-xl bg-sky-950/60 border border-sky-800/40 grid place-items-center">
              <CloudSun className="h-5 w-5 text-sky-400" />
            </div>
            <h3 className="font-display font-semibold text-lg text-zinc-900 mt-5">
              Live weather intelligence
            </h3>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Automatically adjusts your ingredient prep when Christchurch or Auckland experiences sudden rain or heatwaves.
            </p>
          </div>

          <div className="mt-6 bg-zinc-50 border border-zinc-200 rounded-xl p-3.5 space-y-2 text-xs">
            <div className="flex justify-between items-center text-zinc-700">
              <span>Temp &gt; 22°C</span>
              <span className="text-sky-600 font-bold">+42% Cold Drinks</span>
            </div>
            <div className="flex justify-between items-center text-zinc-700">
              <span>Rain &gt; 4mm</span>
              <span className="text-amber-600 font-bold">+18% Comfort Food</span>
            </div>
            <div className="flex justify-between items-center text-zinc-700">
              <span>Wind &gt; 35km/h</span>
              <span className="text-red-600 font-bold">-14% Outdoor Seating</span>
            </div>
          </div>
        </motion.div>

        {/* Tile 3: Automated Purchasing & POs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.15 }}
          className="bg-white border border-zinc-200/90 rounded-2xl p-7 shadow-card flex flex-col justify-between"
        >
          <div>
            <div className="h-10 w-10 rounded-xl bg-emerald-950/60 border border-emerald-800/40 grid place-items-center">
              <ShoppingCart className="h-5 w-5 text-emerald-400" />
            </div>
            <h3 className="font-display font-semibold text-lg text-zinc-900 mt-5">
              Transparent purchasing orders
            </h3>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Recommends exact supplier order amounts with clear reasoning. Accept with 1 click and auto-generate supplier POs.
            </p>
          </div>

          <div className="mt-6 p-3.5 rounded-xl bg-emerald-50/70 border border-emerald-200/70 text-xs text-emerald-900">
            <div className="font-semibold flex items-center gap-1.5 text-emerald-800">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Auto-Balanced Roastery Order
            </div>
            <div className="mt-1 text-emerald-700">
              Recommends <strong>15kg Beans</strong> to prevent Friday 11:30 AM stockout.
            </div>
          </div>
        </motion.div>

        {/* Tile 4: AI Daily Operations Briefing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.2 }}
          className="bg-white border border-zinc-200/90 rounded-2xl p-7 shadow-card flex flex-col justify-between"
        >
          <div>
            <div className="h-10 w-10 rounded-xl bg-amber-950/60 border border-amber-800/40 grid place-items-center">
              <Sparkles className="h-5 w-5 text-amber-400" />
            </div>
            <h3 className="font-display font-semibold text-lg text-zinc-900 mt-5">
              AI operations briefing
            </h3>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Plain-language morning briefing telling your head barista and kitchen manager what to focus on before opening.
            </p>
          </div>

          <div className="mt-6 p-3.5 rounded-xl bg-zinc-950 border border-zinc-800 text-xs text-zinc-300">
            <div className="text-amber-400 font-semibold mb-1">Morning Huddle Summary:</div>
            <p className="text-zinc-400 leading-snug">
              "Prep +30% cold brew concentrate. Friday rugby match at nearby stadium starts at 5:00 PM."
            </p>
          </div>
        </motion.div>

        {/* Tile 5: Waste Tracking & Cost Optimization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.25 }}
          className="bg-white border border-zinc-200/90 rounded-2xl p-7 shadow-card flex flex-col justify-between"
        >
          <div>
            <div className="h-10 w-10 rounded-xl bg-red-950/60 border border-red-800/40 grid place-items-center">
              <Leaf className="h-5 w-5 text-red-400" />
            </div>
            <h3 className="font-display font-semibold text-lg text-zinc-900 mt-5">
              Waste reduction analytics
            </h3>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Log daily spoilage and overproduction to identify where money is leaking and feed corrections directly into tomorrow's model.
            </p>
          </div>

          <div className="mt-6 flex items-center justify-between p-3.5 rounded-xl bg-zinc-50 border border-zinc-200 text-xs">
            <div>
              <div className="font-medium text-zinc-800">Weekly Waste Cut</div>
              <div className="text-zinc-400">Average savings per store</div>
            </div>
            <div className="font-display font-bold text-base text-emerald-600 tnum">-.50</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
